import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'
import { Trans } from '@lingui/macro'

export const SmallTable = ({
  category,
  count,
  action,
  totalCount,
  icon: Icon,
  actionLabel,
  caption,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const countNum = Number(count) || 0
  const totalNum = Number(totalCount) || 0
  const isComplete = useMemo(
    () => totalNum === 0 || countNum >= totalNum,
    [countNum, totalNum],
  )
  const progress = totalNum > 0 ? Math.min(100, (countNum / totalNum) * 100) : 100
  const canAction = Boolean(actionLabel && action && !isComplete)
  const titleColor = theme.palette.newColors?.gray || theme.palette.text.primary

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'box-shadow 200ms ease, border-color 200ms ease',
        '&:hover': {
          boxShadow: theme.shadows[2],
          borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 88 }}>
        <Box
          sx={{
            width: 56,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDark ? 'rgba(255,255,255,0.08)' : theme.palette.primary.main,
            color: isDark ? theme.palette.primary.light : theme.palette.common.white,
          }}
        >
          {Icon ? <Icon fontSize="small" /> : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                color: titleColor,
              }}
            >
              {category}
            </Typography>
            <Chip
              size="small"
              label={isComplete ? <Trans>Complete</Trans> : <Trans>In progress</Trans>}
              color={isComplete ? 'success' : 'warning'}
              variant={isComplete ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 11, height: 24 }}
            />
          </Box>

          {totalNum > 0 ? (
            <>
              <LinearProgress
                variant="determinate"
                value={progress}
                color={isComplete ? 'success' : 'primary'}
                sx={{
                  height: 6,
                  borderRadius: 999,
                  bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {countNum}/{totalNum} <Trans>completed</Trans>
              </Typography>
              {caption ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {caption}
                </Typography>
              ) : null}
            </>
          ) : (
            caption ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {caption}
              </Typography>
            ) : null
          )}
        </Box>

        {canAction ? (
          <Box
            component="button"
            type="button"
            onClick={action}
            sx={{
              border: 'none',
              borderLeft: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer',
              px: 2.5,
              minWidth: 148,
              background: theme.palette.newColors?.pinkGradient,
              color: theme.palette.common.white,
              fontWeight: 700,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              transition: 'opacity 150ms ease',
              '&:hover': { opacity: 0.9 },
            }}
          >
            {actionLabel}
          </Box>
        ) : null}
      </Box>
    </Paper>
  )
}
